import React, { useState, useEffect, useRef } from "react";
import { getTags } from "../../../api/tag.service";
import { ITag } from "../../../model/interface/tag";
import {
  PencilIcon,
  ApproveIcon,
  CrossIcon,
  TagIcon,
} from "../../../assets/icons";
import { useNavigate } from "react-router";
import constant from "../../../utils/constant";
import { TagColor } from "../../../model/tagColor";
import { ITagItem } from "../../../model/shared/tagItem";

enum TagSelectorMode {
  Filter,
  Edit,
}

const TagSelector: React.FC<any> = (props: {
  label: string;
  categoryId: number;
  selectedTagIds?: number[];
  mode: TagSelectorMode;
  onTagSelectChange: (tags: ITagItem[]) => void;
  removeTag: (allTags: ITagItem[], tag: ITagItem) => void;
  onFilterUntagged: () => void;
}) => {
  const [tags, setTags] = useState<ITagItem[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [manageTag, setManageTag] = useState<boolean>(false);
  const tagRef: any = useRef(null);
  const tagIconRef: any = useRef(null);
  const navigate = useNavigate();

  // useEffect(() => {
  //   getTags(tagQuery).then((resp) => {
  //     const tags: ITagItem[] = resp.data.data.map((t) => {
  //       return {
  //         tagId: t.tagId,
  //         name: t.name,
  //         color: t.color,
  //         selected:
  //           props.selectedTagIds && props.selectedTagIds.indexOf(t.tagId) >= 0,
  //         contentAreaId: t.contentAreaId,
  //         contentAreaName: t.contentAreaName,
  //         categoryId: t.categoryId,
  //         categoryName: t.categoryName,
  //         description: t.description,
  //         isActive: t.isActive,
  //       };
  //     });
  //     setTags(tags);
  //   });
  // }, []);

  useEffect(() => {
    if (props?.categoryId) {
      const tagQuery: ITag = {
        contentAreaId: "0",
        categoryId: props?.categoryId?.toString(),
        name: "",
        isActive: true,
        pageSize: 10000,
        pageNumber: 1,
        orderbyColumn: "Name",
        sortDir: "ASC",
      };
      getTags(tagQuery).then((resp) => {
        const tags: ITagItem[] = resp.data.data.map((t) => {
          return {
            tagId: t.tagId,
            name: t.name,
            color: t.color,
            selected:
              props.selectedTagIds &&
              props.selectedTagIds.indexOf(t.tagId) >= 0,
            contentAreaId: t.contentAreaId,
            contentAreaName: t.contentAreaName,
            categoryId: t.categoryId,
            categoryName: t.categoryName,
            description: t.description,
            isActive: t.isActive,
          };
        });
        setTags(tags);
      });
    }
  }, [props.categoryId]);

  useEffect(() => {
    handleSelectedTags();
  }, [props.selectedTagIds]);

  const handleSelectedTags = () => {
    if (tags.length === 0) {
      const tagQuery: ITag = {
        contentAreaId: "0",
        categoryId: props?.categoryId?.toString(),
        name: "",
        isActive: true,
        pageSize: 10000,
        pageNumber: 1,
        orderbyColumn: "Name",
        sortDir: "ASC",
      };
      getTags(tagQuery).then((res) => {
        const data: ITagItem[] = res.data.data.map((t) => {
          return {
            tagId: t.tagId,
            name: t.name,
            color: t.color,
            selected:
              props.selectedTagIds &&
              props.selectedTagIds.indexOf(t.tagId) >= 0,
            contentAreaId: t.contentAreaId,
            contentAreaName: t.contentAreaName,
            categoryId: t.categoryId,
            categoryName: t.categoryName,
            description: t.description,
            isActive: t.isActive,
          };
        });
        setTags(data);
      });
    } else {
      const newTags = tags.map((t) => {
        t.selected = props.selectedTagIds
          ? props.selectedTagIds.indexOf(t.tagId) >= 0
          : false;
        return t;
      });
      setTags(newTags);
    }
  };

  const handleSelectChangeClick = (e, tag, selected) => {
    const allTags = tags.map((item) => {
      if (item.tagId === tag.tagId) {
        item.selected = selected;
      }
      return item;
    });
    setTags(allTags);
    if (typeof props.onTagSelectChange == "function") {
      if (selected) {
        props.onTagSelectChange(tags);
      } else {
        props.removeTag(allTags, tag);
      }
    }
  };

  const handleFilterUntagged = () => {
    if (typeof props.onTagSelectChange == "function") props.onFilterUntagged();
  };

  const routeToTags = () => {
    navigate(constant.TAGS);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        tagRef.current &&
        !tagRef.current.contains(event.target) &&
        tagIconRef.current &&
        !tagIconRef.current.contains(event.target)
      ) {
        setManageTag(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [tagRef]);

  return (
    <>
      <div className="flex flex-col border p-2 rounded-lg border-gray-300">
        <div
          className="flex gap-1 text-sm text-gray-700 cursor-pointer"
          onClick={() => {
            setManageTag(!manageTag);
          }}
          ref={tagIconRef}
        >
          <TagIcon className="h-5 w-5 cursor-pointer text-gray-400 hover:text-gray-600" />
          {props.label}
          {props.mode == TagSelectorMode.Edit && (
            <>
              <div className={"flex items-center"}>
                <div className={"flex-row w-8"} onClick={routeToTags}>
                  <PencilIcon />
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex gap-1 flex-wrap">
          {tags
            .filter((item) => item.selected === true)
            .map((item) => (
              <div key={item.tagId} className={"flex h-8 items-center"}>
                <div className={"flex flex-1 min-w-0"}>
                  <span
                    className={`inline-block bg-[${item.color}] ${
                      TagColor.isDark(item.color) ? "text-white" : ""
                    } rounded-full text-xs shadow-sm py-0.5 px-2 overflow-hidden truncate flex`}
                  >
                    {item.name}
                    <div className={"flex w-4 ml-1 justify-center"}>
                      {item.selected && (
                        <CrossIcon
                          className={
                            "w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                          }
                          onClick={(e) => {
                            handleSelectChangeClick(e, item, false);
                          }}
                        ></CrossIcon>
                      )}
                    </div>
                  </span>
                </div>
              </div>
            ))}
        </div>
        {manageTag && (
          <div
            className=" bg-white absolute z-50  border shadow-md"
            ref={tagRef}
          >
            <div className="flex-col relative">
              <div className="px-4 py-2 text-sm text-gray-700">
                {props.label}
              </div>
              <div className="px-4 border-b border-t py-2">
                <input
                  id="search"
                  name="search"
                  type="text"
                  autoComplete="off"
                  value={filter}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm "
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>
              {props.mode == TagSelectorMode.Filter && (
                <div
                  className={"flex flex-row cursor-pointer pl-8 h-8 mt-2"}
                  onClick={handleFilterUntagged}
                >
                  Untagged
                </div>
              )}

              <div
                className={
                  "h-80 overflow-y-auto divide-y divide-solid border-b  z-20 bg-white  shadow-md top-11 w-full"
                }
              >
                {tags
                  .filter(
                    (tag) =>
                      tag.name.toUpperCase().indexOf(filter.toUpperCase()) >= 0
                  )
                  .sort((a, b) => {
                    return a.selected == b.selected
                      ? a.name.localeCompare(b.name)
                      : a.selected
                      ? -1
                      : 1;
                  })
                  .map((tag) => (
                    <div key={tag.tagId} className={"flex h-8 items-center"}>
                      <div className={"flex w-8 justify-center"}>
                        {tag.selected && <ApproveIcon></ApproveIcon>}
                      </div>
                      <div
                        className={"flex flex-1 min-w-0 cursor-pointer"}
                        onClick={(e) => handleSelectChangeClick(e, tag, true)}
                      >
                        <span
                          className={`inline-block bg-[${tag.color}] ${
                            TagColor.isDark(tag.color) ? "text-white" : ""
                          } rounded-full text-xs shadow-sm py-0.5 px-3 overflow-hidden truncate`}
                        >
                          {tag.name}
                        </span>
                      </div>
                      <div className={"flex w-8 justify-center"}>
                        {tag.selected && (
                          <CrossIcon
                            className={
                              "w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                            }
                            onClick={(e) =>
                              handleSelectChangeClick(e, tag, false)
                            }
                          ></CrossIcon>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TagSelector;
export { TagSelectorMode };
